import { Configuration, LogLevel, PublicClientApplication } from "@azure/msal-browser";
import { selectInTenantAccount } from "./accountSelection";

const tenantId = import.meta.env.VITE_AZURE_TENANT_ID || "";

export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || "",
    authority: import.meta.env.VITE_AZURE_AUTHORITY
      || `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || ""}`,
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin,
    postLogoutRedirectUri: `${import.meta.env.VITE_AZURE_REDIRECT_URI || window.location.origin}/signed-out`,
  },
  cache: {
    cacheLocation: "localStorage",
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
    },
  },
};

export const loginRequest = {
  scopes: [import.meta.env.VITE_AZURE_API_SCOPE || ""],
};

// Persist the user's login hint in a cookie so loginRedirect can skip
// the account picker on subsequent app launches. Cookies on localhost
// are shared across ports, unlike localStorage which is origin-scoped.
export function getLoginHint(): string | undefined {
  const match = document.cookie.match(/(?:^|;\s*)msal_login_hint=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

function saveLoginHint(username: string): void {
  document.cookie = `msal_login_hint=${encodeURIComponent(username)}; path=/; max-age=31536000; SameSite=Lax`;
}

export function clearLoginHint(): void {
  document.cookie = "msal_login_hint=; path=/; max-age=0; SameSite=Lax";
}

// Single shared MSAL instance used by all modules
export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL and handle redirect responses; resolves before the
// React app renders (awaited in index.tsx).
export const msalInitPromise = msalInstance
  .initialize()
  .then(() => msalInstance.handleRedirectPromise())
  .then((response) => {
    // A redirect just completed. Honour the returned account only if it belongs to this tenant —
    // otherwise the user picked a foreign-tenant account (the app is multi-tenant); don't activate it,
    // and drop any stale login hint so the next sign-in shows the account picker rather than silently
    // re-selecting the wrong one.
    if (response?.account) {
      if (response.account.tenantId === tenantId) {
        msalInstance.setActiveAccount(response.account);
        if (response.account.username) saveLoginHint(response.account.username);
      } else {
        clearLoginHint();
      }
      return;
    }

    // No redirect response: activate a cached account, but strictly one from this tenant. If only a
    // foreign-tenant (or no) account is cached, leave the active account unset so App triggers an
    // interactive sign-in instead of getting stuck on an account whose tokens the API will reject.
    const active = selectInTenantAccount(msalInstance.getAllAccounts(), tenantId);
    if (active) {
      msalInstance.setActiveAccount(active);
      if (active.username) saveLoginHint(active.username);
    }
  })
  .catch((error) => {
    console.error("MSAL initialization failed:", error);
  });
